import React from 'react'
import { Grid } from '@mui/material'
import { PayPeriodData } from '../../../models/enhancement/pay-period-data'
import { usePayPeriods } from '../hooks/usePayPeriods'
import { usePayPeriodPickerModal } from './hooks/usePayPeriodPickerModal'
import { getFieldProperties, getGridColumns } from './utils/fieldProperties'
import PayPeriodField from './PayPeriodField'
import PayPeriodModal from './PayPeriodModal'

interface PayPeriodPickerProps {
  /** Currently selected "from" pay period */
  fromPeriod?: PayPeriodData | null;
  /** Currently selected "to" pay period */
  toPeriod?: PayPeriodData | null;
  /** Callback when "from" pay period changes */
  onFromChange: (payPeriod: PayPeriodData | null) => void;
  /** Callback when "to" pay period changes */
  onToChange: (payPeriod: PayPeriodData | null) => void;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Data test ID for testing */
  dataTestId?: string;
  /** Layout arrangement */
  arrange?: 'Horizontal' | 'Stack';
  /** Grid spacing */
  spacing?: 1 | 2 | 3;
}

/**
 * PayPeriodPicker component that provides pay period selection for both from and to fields
 *
 * Features:
 * - Two PayPeriodField components for from/to selection
 * - Single modal shared between both fields
 * - Data fetching and state management
 * - Follows same pattern as DateRangePicker for consistency
 */
function PayPeriodPicker({
  fromPeriod,
  toPeriod,
  onFromChange,
  onToChange,
  disabled = false,
  dataTestId = 'pay-period-picker',
  arrange = 'Stack',
  spacing = 3
}: PayPeriodPickerProps) {
  
  const {
    payPeriodsByYear,
    availableYears,
    isLoading,
    error
  } = usePayPeriods()

  const {
    modalOpen,
    anchorEl,
    selectedYear,
    handleOpenModal,
    handleCloseModal,
    handleSelectPayPeriod,
    handleYearChange
  } = usePayPeriodPickerModal({
    onFromChange,
    onToChange,
    fromPeriod,
    toPeriod,
    disabled,
    isLoading
  })

  const columns = getGridColumns(arrange)
  const fieldPropsInput = { disabled, isLoading, error, dataTestId }
  const fromFieldProps = getFieldProperties(fieldPropsInput, 'from')
  const toFieldProps = getFieldProperties(fieldPropsInput, 'to')

  return (
    <>
      <Grid container spacing={spacing} data-testid={dataTestId}>
        <Grid item xs={columns}>
          <PayPeriodField
            label="Pay period from"
            value={fromPeriod}
            onClick={(event) => handleOpenModal('from', event)}
            {...fromFieldProps}
          />
        </Grid>
        
        <Grid item xs={columns}>
          <PayPeriodField
            label="Pay period to"
            value={toPeriod}
            onClick={(event) => handleOpenModal('to', event)}
            {...toFieldProps}
          />
        </Grid>
      </Grid>

      <PayPeriodModal
        open={modalOpen}
        anchorEl={anchorEl}
        onClose={handleCloseModal}
        onSelect={handleSelectPayPeriod}
        payPeriodsByYear={payPeriodsByYear}
        availableYears={availableYears}
        selectedYear={selectedYear}
        onYearChange={handleYearChange}
        isLoading={isLoading}
        error={error}
        dataTestId={`${dataTestId}-modal`}
      />
    </>
  )
}

export default PayPeriodPicker